import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import api from "../api";
import { setActiveProject, clearActiveProject } from "../redux/projectSlice";
import { setSessionId } from "../redux/sessionSlice";
import { setRefreshSidebar } from "../redux/sidebarSlice";
import {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "../utils/toastUtils";
import CreateProjectModal from "../components/CreateProjectModal";
import {
  fetchUploadLimits,
  DEFAULT_UPLOAD_LIMITS,
  getSizeLimitBytes,
  getSizeLimitMb,
  getAllowedExtensions,
} from "../utils/uploadLimits";
import { validateFileName, truncate } from "../utils/validateFileName";
import "../css/ProjectWorkspace.css";
import { Loader2 } from "lucide-react";
import sendIcon from "../assets/UserIcons/SendChatIcon.svg";

const getFileExtLabel = (name = "") => {
  const ext = String(name).split(".").pop()?.toLowerCase();
  if (!ext || ext === String(name).toLowerCase()) return "FILE";
  const map = {
    pdf: "PDF",
    doc: "DOC",
    docx: "DOCX",
    txt: "TXT",
    md: "MD",
    csv: "CSV",
    xls: "XLS",
    xlsx: "XLSX",
    ppt: "PPT",
    pptx: "PPTX",
    png: "IMG",
    jpg: "IMG",
    jpeg: "IMG",
    webp: "IMG",
  };
  return map[ext] || ext.toUpperCase();
};

const formatBytes = (bytes = 0) => {
  bytes = Number(bytes);
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const diffMs = date.getTime() - Date.now();
  const diffMinutes = Math.round(diffMs / 60000);
  const diffHours = Math.round(diffMs / 3600000);
  const diffDays = Math.round(diffMs / 86400000);

  if (Math.abs(diffMinutes) < 60) {
    return diffMinutes >= 0
      ? `in ${Math.max(1, diffMinutes)} min`
      : `${Math.max(1, Math.abs(diffMinutes))} min ago`;
  }
  if (Math.abs(diffHours) < 24) {
    return diffHours >= 0
      ? `in ${Math.max(1, diffHours)} hr`
      : `${Math.max(1, Math.abs(diffHours))} hr ago`;
  }
  return diffDays >= 0
    ? `in ${Math.max(1, diffDays)} days`
    : `${Math.max(1, Math.abs(diffDays))} days ago`;
};

const getRecentChatLabel = (session) =>
  session?.first_input?.trim() || session?.session_name?.trim() || "Chat";

const LENGTH_OPTIONS = [
  { value: "long", label: "Long" },
  { value: "deep", label: "Deep" },
  { value: "medium", label: "Short" },
];

const AI_ROLE_OPTIONS = [
  { value: "default", label: "General Assistant (Default)" },
  { value: "Brainstorm partner", label: "Brainstorm partner" },
  { value: "Career coach", label: "Career coach" },
  { value: "Chef", label: "Chef" },
  { value: "Coding coach", label: "Coding coach" },
  { value: "Editor", label: "Editor" },
  { value: "Entertainment guide", label: "Entertainment guide" },
  { value: "Fitness trainer", label: "Fitness trainer" },
  { value: "Gardener", label: "Gardener" },
  { value: "Homework helper", label: "Homework helper" },
  { value: "Language tutor", label: "Language tutor" },
  { value: "Life coach", label: "Life coach" },
  { value: "Marketer", label: "Marketer" },
  { value: "Product manager", label: "Product manager" },
  { value: "Public speaking coach", label: "Public speaking coach" },
  { value: "Social media copywriter", label: "Social media copywriter" },
  { value: "Storyteller", label: "Storyteller" },
  { value: "Tech support specialist", label: "Tech support specialist" },
  { value: "Teacher", label: "Teacher" },
  { value: "Translator", label: "Translator" },
  { value: "Travel guide", label: "Travel guide" },
  { value: "Trivia expert", label: "Trivia expert" },
  { value: "Writer", label: "Writer" },
];

const USER_ROLE_OPTIONS = [
  { value: "default", label: "Universal (Default)" },
  { value: "Parent", label: "Parent" },
  { value: "Professional", label: "Professional" },
  { value: "Programmer", label: "Programmer" },
  { value: "Student", label: "Student" },
  { value: "Writer", label: "Writer" },
];

const TONE_MODE_OPTIONS = [
  { value: "default", label: "Neutral (Default)" },
  { value: "eli5", label: "Simple" },
  { value: "tldr", label: "Summary" },
  { value: "jargonize", label: "Professional" },
  { value: "humanize", label: "Naturally" },
  { value: "feynman", label: "Deep" },
];

const ProjectWorkspace = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedModel, selectedMode } = useSelector((state) => state.model);
  const {
    aiPreferences,
    setAiPreferences,
    DEFAULT_AI_PREFERENCES,
  } = useOutletContext() || {};

  const [project, setProject] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [draftMessage, setDraftMessage] = useState("");
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [micOpen, setMicOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [tempTranscript, setTempTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [isProcessingSTT, setIsProcessingSTT] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [localPrefs, setLocalPrefs] = useState(
    aiPreferences || DEFAULT_AI_PREFERENCES || {},
  );
  const [contextFiles, setContextFiles] = useState([]);

  const [openRecentMenuId, setOpenRecentMenuId] = useState(null);
  const [showSessionRenameModal, setShowSessionRenameModal] = useState(false);
  const [showSessionDeleteModal, setShowSessionDeleteModal] = useState(false);
  const [renameSessionTarget, setRenameSessionTarget] = useState(null);
  const [renameSessionValue, setRenameSessionValue] = useState("");
  const [deleteSessionTarget, setDeleteSessionTarget] = useState(null);
  const [savingSessionAction, setSavingSessionAction] = useState(false);
  const [uploadLimits, setUploadLimits] = useState(DEFAULT_UPLOAD_LIMITS);

  const projectCapacityBytes = useMemo(() => {
    const rawValue =
      Number(uploadLimits?.project_capacity_limit_value) ||
      Number(uploadLimits?.project_capacity_limit_mb) ||
      75;
    const unit = String(uploadLimits?.project_capacity_limit_unit || "MB").toUpperCase();
    const value = rawValue > 0 ? rawValue : 75;
    return unit === "GB" ? value * 1024 * 1024 * 1024 : value * 1024 * 1024;
  }, [uploadLimits]);

  const projectMaxFiles = Number(uploadLimits?.project_max_files_per_project) || 20;

  const menuRef = useRef(null);
  const messageFileInputRef = useRef(null);
  const contextFileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const contextPollingIntervalsRef = useRef({});

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}`);
      const proj = res.data?.project;
      setProject(proj);
      dispatch(setActiveProject(proj));
    } catch (err) {
      console.error("Failed to fetch project:", err);
      showErrorToast("Project not found");
      navigate("/user/projects", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [projectId, dispatch, navigate]);

  // Fetch project context files from backend
  const fetchContextFiles = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/projects/${projectId}/context/files`);
      setContextFiles(res.data?.files || []);
    } catch (err) {
      console.error("Failed to fetch project context files:", err);
    }
  }, [projectId]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get(`/projects/${projectId}/sessions`);
      setSessions(res.data?.sessions || []);
    } catch (err) {
      console.error("Failed to fetch project sessions:", err);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
    fetchSessions();
  }, [fetchProject, fetchSessions]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    const handleRecentMenuOutsideClick = (event) => {
      if (!event.target.closest(".project-recent-menu-wrapper")) {
        setOpenRecentMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleRecentMenuOutsideClick);
    return () =>
      document.removeEventListener("mousedown", handleRecentMenuOutsideClick);
  }, []);

  useEffect(() => {
    const intervals = contextPollingIntervalsRef.current;
    return () => {
      // Clear all polling intervals on unmount
      Object.keys(intervals).forEach((key) => {
        clearInterval(intervals[key]);
      });
      dispatch(clearActiveProject());
    };
  }, [dispatch]);

  // Enterprise-configured upload limits (max file count + max size)
  useEffect(() => {
    fetchUploadLimits().then(setUploadLimits);
  }, []);

  const MAX_ATTACHABLE_FILES =
    Number(uploadLimits?.max_upload_file_count) || 20;

  const allowedExtensions = getAllowedExtensions(uploadLimits);

  const getSizeLimit = (filename) => getSizeLimitBytes(filename, uploadLimits);

  const getSizeLimitLabel = (filename) =>
    `${getSizeLimitMb(filename, uploadLimits)} MB`;

  const filterSupportedFiles = (files) => {
    const supported = [];
    const unsupported = [];
    const oversized = [];

    files.forEach((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        unsupported.push(file);
        return;
      }
      const sizeLimit = getSizeLimit(file.name);
      if (file.size > sizeLimit) {
        oversized.push({
          name: file.name,
          size: file.size,
          limitLabel: getSizeLimitLabel(file.name),
        });
        return;
      }
      supported.push(file);
    });

    return { supported, unsupported, oversized };
  };

  const formatToastMessage = (message) => {
    if (!message) return message;
    const fileRegex = /([\w,\s-]+\.[A-Za-z]{2,6})/g;
    return message.replace(fileRegex, (match) => truncate(match));
  };

  useEffect(() => {
    if (project) fetchContextFiles();
  }, [project, fetchContextFiles]);

  useEffect(() => {
    if (showResponsesModal && aiPreferences) {
      setLocalPrefs(aiPreferences);
    }
  }, [showResponsesModal, aiPreferences]);

  useEffect(() => {
    if (!showResponsesModal) setShowInfoTooltip(false);
  }, [showResponsesModal]);

  // Poll ingestion status for processing files
  useEffect(() => {
    const intervals = contextPollingIntervalsRef.current;
    const activeKeys = new Set();

    const isTerminal = (s) =>
      s === "completed" || s === "failed" || s === "cancelled";

    contextFiles.forEach((file) => {
      const key = file.file_id;

      // Already has a running interval — skip
      if (intervals[key]) return;

      // Only poll files that are in an active state with a job_id
      if (!file.job_id) return;
      if (isTerminal(file.status) || file.progress === 100) return;

      const poll = async () => {
        try {
          const res = await api.get(
            `/projects/ingestion/status/${file.job_id}`,
          );
          const { status, progress, document_id, failure_reason } =
            res.data || {};

          const shouldStop = isTerminal(status) || progress === 100;

          if (shouldStop && intervals[key]) {
            clearInterval(intervals[key]);
            delete intervals[key];
            activeKeys.delete(key);
          }

          setContextFiles((prev) =>
            prev.map((f) =>
              f.file_id === key
                ? {
                    ...f,
                    status,
                    progress,
                    document_id: document_id || f.document_id,
                    failure_reason: failure_reason || f.failure_reason,
                  }
                : f,
            ),
          );

          if (status === "completed") {
            showSuccessToast(
              `"${file.original_filename}" processing completed.`,
            );
          } else if (status === "failed") {
            showErrorToast(
              `"${file.original_filename}" failed: ${failure_reason || "Unknown error"}`,
            );
          }
        } catch (err) {
          console.error("Ingestion poll error:", err);
        }
      };

      // Fire initial poll only once per file across the entire session
      const pollKey = `_didInitial_${key}`;
      if (!intervals[pollKey]) {
        intervals[pollKey] = true;
        poll();
      }
      intervals[key] = setInterval(poll, 3000);
      activeKeys.add(key);
    });

    return () => {
      activeKeys.forEach((key) => {
        if (intervals[key]) {
          clearInterval(intervals[key]);
          delete intervals[key];
        }
      });
    };
  }, [contextFiles]);

  const handleNewChat = () => {
    const trimmedMessage = draftMessage.trim();
    const attachmentNames = messageAttachments
      .map((item) => item.name)
      .join(", ");
    const firstMessage = trimmedMessage
      ? attachmentNames
        ? `${trimmedMessage} (Attached files: ${attachmentNames})`
        : trimmedMessage
      : attachmentNames
        ? `Please review the attached files in this project. (Attached files: ${attachmentNames})`
        : "";

    navigate(`/user/projects/${projectId}/new`, {
      state: { firstMessage, attachedFilesFromHome: messageAttachments },
    });
  };

  const handleOpenChat = (sessionId) => {
    dispatch(setSessionId(sessionId));
    const session = sessions.find((item) => item.session_id === sessionId);
    navigate(`/user/projects/${projectId}/new`, {
      state: {
        sessionId,
        chatTitle:
          session?.session_name ||
          session?.first_input?.slice(0, 30) ||
          "New Chat",
      },
    });
  };

  const handleToggleRecentMenu = (e, sessionId) => {
    e.stopPropagation();
    setOpenRecentMenuId((prev) => (prev === sessionId ? null : sessionId));
  };

  const handleOpenRenameSession = (e, session) => {
    e.stopPropagation();
    setOpenRecentMenuId(null);
    setRenameSessionTarget(session);
    setRenameSessionValue(
      (session?.session_name || session?.first_input || "").trim(),
    );
    setShowSessionRenameModal(true);
  };

  const handleOpenDeleteSession = (e, session) => {
    e.stopPropagation();
    setOpenRecentMenuId(null);
    setDeleteSessionTarget(session);
    setShowSessionDeleteModal(true);
  };

  const handleConfirmRenameSession = async () => {
    const trimmed = renameSessionValue.trim();
    if (!renameSessionTarget || !trimmed) return;

    try {
      setSavingSessionAction(true);
      await api.put(`${process.env.REACT_APP_API_URL}/chat/sessions/update`, {
        session_id: renameSessionTarget.session_id,
        session_name: trimmed,
      });

      setSessions((prev) =>
        prev.map((session) =>
          session.session_id === renameSessionTarget.session_id
            ? { ...session, session_name: trimmed }
            : session,
        ),
      );
      showSuccessToast("Chat renamed");
      setShowSessionRenameModal(false);
      setRenameSessionTarget(null);
    } catch (error) {
      console.error("Failed to rename chat:", error);
      showErrorToast("Failed to rename chat");
    } finally {
      setSavingSessionAction(false);
    }
  };

  const handleConfirmDeleteSession = async () => {
    if (!deleteSessionTarget) return;

    try {
      setSavingSessionAction(true);
      await api.delete(
        `${process.env.REACT_APP_API_URL}/chat/history/${deleteSessionTarget.session_id}`,
      );

      setSessions((prev) =>
        prev.filter(
          (session) => session.session_id !== deleteSessionTarget.session_id,
        ),
      );
      showSuccessToast("Chat deleted");
      setShowSessionDeleteModal(false);
      setDeleteSessionTarget(null);
    } catch (error) {
      console.error("Failed to delete chat:", error);
      showErrorToast("Failed to delete chat");
    } finally {
      setSavingSessionAction(false);
    }
  };

  const handleEditClick = () => {
    setShowMenu(false);
    setShowEditModal(true);
  };

  const handleEditClose = (saved) => {
    setShowEditModal(false);
    if (saved) {
      fetchProject();
    }
  };

  const handleDeleteClick = () => {
    setShowMenu(false);
    setShowDeleteModal(true);
  };

  const handleTogglePin = async () => {
    setShowMenu(false);
    try {
      const res = await api.put(`/projects/${project.project_id}/pin`);
      const newPinned = res.data?.is_pinned;
      setProject((prev) => ({ ...prev, is_pinned: newPinned }));
      dispatch(setRefreshSidebar(true));
      showSuccessToast(newPinned ? "Project pinned" : "Project unpinned");
    } catch (err) {
      showErrorToast("Failed to update pin status");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/projects/${projectId}`);
      showSuccessToast("Project deleted");
      dispatch(setRefreshSidebar(true));
      dispatch(clearActiveProject());
      navigate("/user/projects", { replace: true });
    } catch (err) {
      showErrorToast("Failed to delete project");
    } finally {
      setShowDeleteModal(false);
    }
  };

  // Trigger file input click (same pattern as Home.js)
  const handleAttachClick = () => {
    messageFileInputRef.current?.click();
  };

  // Handle file selection — validates files then uploads to FastAPI
  const handleMessageFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (contextFiles.length + files.length > projectMaxFiles) {
      showErrorToast(
        `This project can contain a maximum of ${projectMaxFiles} files. You currently have ${contextFiles.length}.`,
      );
      e.target.value = "";
      return;
    }

    const { supported, unsupported, oversized } = filterSupportedFiles(files);

    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
    }

    if (oversized.length > 0) {
      oversized.forEach((f) => {
        const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
        showErrorToast(
          `"${f.name}" (${sizeMB} MB) exceeds the ${f.limitLabel} size limit.`,
        );
      });
    }

    if (supported.length === 0) {
      e.target.value = "";
      return;
    }

    // Check max file count
    const currentFileCount = messageAttachments.length;
    if (currentFileCount >= MAX_ATTACHABLE_FILES) {
      showErrorToast(
        `You can upload a maximum of ${MAX_ATTACHABLE_FILES} files at a time.`,
      );
      e.target.value = "";
      return;
    }

    let limitedFiles = supported;
    if (currentFileCount + supported.length > MAX_ATTACHABLE_FILES) {
      const remaining = MAX_ATTACHABLE_FILES - currentFileCount;
      limitedFiles = supported.slice(0, remaining);
      showInfoToast(
        `Added ${remaining} file(s) (limit ${MAX_ATTACHABLE_FILES} files).`,
      );
    }

    // Validate filenames
    const validNameFiles = [];
    const invalidNameFiles = [];

    for (const file of limitedFiles) {
      const { valid, reason } = validateFileName(file.name);
      if (valid) {
        validNameFiles.push(file);
      } else {
        invalidNameFiles.push({ name: file.name, reason });
      }
    }

    if (invalidNameFiles.length > 0) {
      const count = invalidNameFiles.length;
      const autoCloseTime = count <= 2 ? 2000 : count <= 4 ? 1600 : 1200;
      const gap = autoCloseTime + 150;

      invalidNameFiles.forEach((f, index) => {
        setTimeout(() => {
          showErrorToast(`${f.reason}${truncate(f.name)}`, {
            autoClose: autoCloseTime,
          });
        }, index * gap);
      });
    }

    if (validNameFiles.length === 0) {
      e.target.value = "";
      return;
    }

    // Add files as "uploading" and immediately upload to FastAPI
    const tempFiles = validNameFiles.map((file) => ({
      id: `attachment_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      file,
      status: "uploading",
      clientId: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      size: file.size,
      kind: getFileExtLabel(file.name),
    }));

    setMessageAttachments((prev) => [...prev, ...tempFiles]);
    e.target.value = "";

    // Upload to FastAPI
    await uploadFilesToFastAPI(tempFiles);
  };

  // Upload files to FastAPI backend (same as Home.js handleUploadFiles)
  const uploadFilesToFastAPI = async (filesToUpload) => {
    const formData = new FormData();
    filesToUpload.forEach((item) => formData.append("files", item.file));

    formData.append("model", selectedModel);
    formData.append("mode", (selectedMode || "global").toLowerCase());
    formData.append("folder_id", null);

    try {
      const uploadEndpoint = `${process.env.REACT_APP_API_URL}/uploaded-files/newUploadv2`;

      const res = await api.post(uploadEndpoint, formData);

      const { toasts = [] } = res.data;

      if (toasts.length > 0) {
        const count = toasts.length;
        const autoCloseTime = count <= 2 ? 2200 : count <= 4 ? 1800 : 1400;
        const gap = autoCloseTime + 150;

        toasts.forEach((toast, index) => {
          setTimeout(() => {
            const message = formatToastMessage(toast.message);
            if (toast.type === "success") {
              showSuccessToast(message, { autoClose: autoCloseTime });
            } else if (toast.type === "warning") {
              showInfoToast(message, { autoClose: autoCloseTime });
            } else {
              showErrorToast(message, { autoClose: autoCloseTime });
            }
          }, index * gap);
        });
      }

      // Update statuses based on response
      setMessageAttachments((prev) =>
        prev.map((f) => {
          const isTarget = filesToUpload.some((t) => t.clientId === f.clientId);
          if (!isTarget) return f;

          const success = toasts.find(
            (t) =>
              t.type === "success" &&
              t.message.toLowerCase().includes(f.name.toLowerCase()),
          );
          const duplicate = toasts.find(
            (t) =>
              t.message.toLowerCase().includes(f.name.toLowerCase()) &&
              (t.message.includes("already exists") ||
                t.message.includes("pending")),
          );
          const failed = toasts.find(
            (t) =>
              t.type === "error" &&
              t.message.toLowerCase().includes(f.name.toLowerCase()),
          );

          if (success) return { ...f, status: "done" };
          if (duplicate) return { ...f, status: "done" };
          if (failed) return { ...f, status: "error" };
          return { ...f, status: "done" };
        }),
      );
    } catch (error) {
      const backendMessage =
        error.response?.data?.message || error.response?.data?.error;

      showErrorToast(
        backendMessage || "Upload failed. Please try again later.",
      );

      // Mark uploading files as error
      setMessageAttachments((prev) =>
        prev.map((f) =>
          filesToUpload.some((t) => t.clientId === f.clientId)
            ? { ...f, status: "error" }
            : f,
        ),
      );
    }
  };

  // Retry failed uploads
  const retryUpload = async (attachment) => {
    setMessageAttachments((prev) =>
      prev.map((f) =>
        f.id === attachment.id ? { ...f, status: "uploading" } : f,
      ),
    );

    await uploadFilesToFastAPI([{ ...attachment, status: "uploading" }]);
  };

  const handleContextFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";

    // Check total size against project capacity limit
    const filesTotalSize = files.reduce((sum, file) => sum + file.size, 0);
    const currentUsage = contextBytesUsed;
    const availableSpace = projectCapacityBytes - currentUsage;

    if (availableSpace <= 0) {
      showErrorToast(
        "Project context storage is full. Delete some files to free up space.",
      );
      return;
    }

    const exceedsCapacity = filesTotalSize > availableSpace;
    if (exceedsCapacity) {
      const currentUsedStr = formatBytes(currentUsage);
      const newTotalStr = formatBytes(currentUsage + filesTotalSize);
      const capacityStr = formatBytes(projectCapacityBytes);
      showErrorToast(
        `These files would exceed the project storage limit (${capacityStr}). ` +
        `Current usage: ${currentUsedStr}. After upload: ${newTotalStr}.`,
      );
      return;
    }

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.append("model", selectedModel || "gpt-4o-mini");
    formData.append("mode", (selectedMode || "personal").toLowerCase());

    try {
      showInfoToast(`Uploading ${files.length} file(s) to project context...`);

      const res = await api.post(
        `/projects/${projectId}/context/upload`,
        formData,
      );

      const uploadedFiles = res.data?.files || [];

      if (uploadedFiles.length > 0) {
        const successCount = uploadedFiles.filter(
          (f) => f.status !== "failed",
        ).length;
        const failedCount = uploadedFiles.filter(
          (f) => f.status === "failed",
        ).length;

        if (successCount > 0) {
          showSuccessToast(
            `${successCount} file(s) uploaded to project context.`,
          );
        }
        if (failedCount > 0) {
          showErrorToast(`${failedCount} file(s) failed to upload.`);
        }
      }

      // Refresh the context file list from backend
      await fetchContextFiles();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message || err.response?.data?.error;
      showErrorToast(
        backendMessage || "Failed to upload files to project context.",
      );
    }
  };

  // Cancel ingestion for a context file
  const handleCancelIngestion = async (file) => {
    if (!file.job_id) return;
    try {
      await api.post(`/projects/ingestion/cancel/${file.job_id}`);
      setContextFiles((prev) =>
        prev.map((f) =>
          f.file_id === file.file_id ? { ...f, status: "cancelled" } : f,
        ),
      );
      // Stop polling for this file
      const intervals = contextPollingIntervalsRef.current;
      if (intervals[file.file_id]) {
        clearInterval(intervals[file.file_id]);
        delete intervals[file.file_id];
      }
      showInfoToast(`Ingestion cancelled for "${file.original_filename}".`);
    } catch (err) {
      showErrorToast("Failed to cancel ingestion.");
    }
  };

  // Retry a failed context file
  const handleRetryContextFile = async (file) => {
    try {
      const res = await api.post(
        `/projects/${projectId}/context/files/${file.file_id}/retry`,
      );
      const { status, job_id } = res.data || {};
      setContextFiles((prev) =>
        prev.map((f) =>
          f.file_id === file.file_id
            ? {
                ...f,
                status: status || "processing",
                job_id: job_id || f.job_id,
                progress: 10,
                failure_reason: null,
              }
            : f,
        ),
      );
      showInfoToast(`Retrying "${file.original_filename}"...`);
    } catch (err) {
      showErrorToast("Failed to retry ingestion.");
    }
  };

  // Delete a context file
  const handleDeleteContextFile = async (file) => {
    try {
      await api.delete(`/projects/${projectId}/context/files/${file.file_id}`);
      setContextFiles((prev) => prev.filter((f) => f.file_id !== file.file_id));
      showSuccessToast(`"${file.original_filename}" deleted from context.`);
    } catch (err) {
      showErrorToast("Failed to delete file.");
    }
  };

  const handleRemoveAttachment = (attachmentId) => {
    setMessageAttachments((prev) =>
      prev.filter((item) => item.id !== attachmentId),
    );
  };

  const handleDraftKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendDraft();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      setIsRecording(true);
      setInterimTranscript("");
      setTempTranscript("");

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.start();
    } catch (err) {
      console.error("Mic error:", err);
      showErrorToast("Microphone access denied");
    }
  };

  const releaseRecorderTracks = (recorder) => {
    recorder?.stream?.getTracks().forEach((track) => track.stop());
  };

  const stopRecording = async () => {
    setIsRecording(false);
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    recorder.stop();
    recorder.onstop = async () => {
      releaseRecorderTracks(recorder);
      const audioBlob = new Blob(audioChunksRef.current, {
        type: "audio/webm",
      });
      const formData = new FormData();
      formData.append("file", audioBlob, "audio.webm");

      try {
        setIsProcessingSTT(true);
        setInterimTranscript("Processing...");
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/chat/realtime-stt`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          },
        );

        if (!response.ok) throw new Error("STT request failed");
        const data = await response.json();
        const text = data?.transcription?.trim();
        if (text) setDraftMessage((prev) => `${prev}${text} `);
      } catch (err) {
        console.error("STT error:", err);
        showErrorToast("Speech recognition failed");
      } finally {
        setIsProcessingSTT(false);
        setMicOpen(false);
        setInterimTranscript("");
        setTempTranscript("");
        audioChunksRef.current = [];
        mediaRecorderRef.current = null;
      }
    };
  };

  const cancelRecording = () => {
    setIsRecording(false);
    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;
      recorder.onstop = null;
      releaseRecorderTracks(recorder);
      if (recorder.state !== "inactive") recorder.stop();
      mediaRecorderRef.current = null;
    }
    audioChunksRef.current = [];
    setMicOpen(false);
    setInterimTranscript("");
    setTempTranscript("");
  };

  const toggleReadAloud = () => {
    if (!draftMessage.trim()) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(draftMessage.trim());
    utterance.lang = "en-US";
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const handlePreferenceChange = (key, value) => {
    setLocalPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyPreferences = () => {
    setAiPreferences?.(localPrefs);
    setShowResponsesModal(false);
  };

  const handleCancelPreferences = () => {
    setLocalPrefs(aiPreferences || DEFAULT_AI_PREFERENCES || {});
    setShowResponsesModal(false);
  };

  const handleSendDraft = () => {
    const hasMessage = Boolean(draftMessage.trim());
    const hasAttachments = messageAttachments.length > 0;
    if (!hasMessage && !hasAttachments) return;

    handleNewChat();
  };

  const contextBytesUsed = useMemo(
    () => contextFiles.reduce((sum, f) => sum + (Number(f.file_size) || 0), 0),
    [contextFiles],
  );

  const contextUsagePercent = Math.min(
    100,
    Math.max(
      0,
      ((Number(contextBytesUsed) || 0) / projectCapacityBytes) * 100,
    ),
  );

  if (loading) {
    return (
      // <div className="project-workspace-loading">
      <div className="repo-loader-container">
        {/* <div className="spinner-border" role="status" /> */}
        <Loader2 className="animate-spin" size={32} />
        <p className="mt-2 ">Loading Project Workspace...</p>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="project-workspace-page overflow-hidden px-3">
      <div className="project-workspace-shell p-2">
        <div className="project-workspace-topbar">
          <div className="project-breadcrumb ak-text-lg fw-bold">
            <span
              style={{ cursor: "pointer", textDecoration: "underline" }}
              onClick={() => navigate("/user/projects")}
            >
              Projects
            </span>
            {" "}/ {project.name}
          </div>
          <div className="project-topbar-actions" ref={menuRef}>
            <button
              className="project-menu-trigger"
              type="button"
              onClick={() => setShowMenu((prev) => !prev)}
              aria-label="Project actions"
            >
              <i className="bi bi-three-dots" />
            </button>

            {showMenu && (
              <div className="project-menu-popover">
                {/* <button type="button" onClick={handleEditClick}>
                  <i className="bi bi-gear me-2" />
                  Manage project
                </button> */}
                <button type="button" onClick={handleEditClick}>
                  <i className="bi bi-pencil me-2" />
                  Edit project
                </button>
                <button type="button" onClick={handleTogglePin}>
                  <i className="bi bi-pin-angle me-2" />
                  {project.is_pinned ? "Unpin project" : "Pin project"}
                </button>
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="danger"
                >
                  <i className="bi bi-trash me-2" />
                  Delete project
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="project-workspace-header">
          <div className="project-workspace-heading d-flex gap-1 flex-column">
            <h1 className="ak-text-lg d-flex align-items-center gap-2">
              {project.name}
              {project.is_pinned && (
                <i
                  className="bi bi-pin-angle-fill"
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--text-muted)",
                    display: "inline-block",
                  }}
                  title="Pinned"
                />
              )}
            </h1>
            {project.description && (
              <p
                className="ak-text-md custom-ak-scollbar m-0"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {project.description}
              </p>
            )}
            <div className="project-workspace-meta">
              <span>
                <i className="bi bi-chat-dots me-1" />
                {project.chat_count}{" "}
                {project.chat_count === 1 ? "chat" : "chats"}
              </span>
              <span>
                <i className="bi bi-calendar me-1" />
                Created {formatDate(project.created_timestamp)}
              </span>
            </div>
          </div>
        </div>

        <div className="project-workspace-grid mt-2 ">
          <div className="project-workspace-main">
            <div className="project-chat-composer input-area mt-4">
              <textarea
                className="p-2 border-0 shadow-none input-textarea project-chat-input"
                placeholder={
                  isComposerFocused ? "" : `New chat in ${project.name}`
                }
                value={draftMessage}
                onChange={(e) => {
                  setDraftMessage(e.target.value);
                  e.target.style.height = "auto";
                  e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
                  e.target.style.overflowY =
                    e.target.scrollHeight > 200 ? "auto" : "hidden";
                }}
                onFocus={() => setIsComposerFocused(true)}
                onBlur={() => setIsComposerFocused(false)}
                onKeyDown={handleDraftKeyDown}
                rows={1}
              />

              {messageAttachments.length > 0 && (
                <div className="project-attachment-chips custom-ak-scollbar">
                  {messageAttachments.map((attachment) => (
                    <span
                      key={attachment.id}
                      className="project-attachment-chip"
                    >
                      {attachment.status === "uploading" ? (
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                        />
                      ) : attachment.status === "error" ? (
                        <i
                          className="bi bi-arrow-clockwise text-danger me-1"
                          style={{ cursor: "pointer" }}
                          title="Retry upload"
                          onClick={() => retryUpload(attachment)}
                        />
                      ) : (
                        <i className="bi bi-check-circle-fill text-success me-1" />
                      )}
                      <span>{attachment.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(attachment.id)}
                        aria-label={`Remove ${attachment.name}`}
                      >
                        <i className="bi bi-x" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="d-flex align-items-center justify-content-between p-1 rounded-3 project-chat-toolbar">
                <div className="d-flex gap-2 project-chat-tools">
                  <div className="position-relative">
                    <button
                      type="button"
                      className="btn p-0 chat-btn-message"
                      title="Customize Responses"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowResponsesModal((prev) => !prev);
                      }}
                    >
                      <i className="bi bi-sliders fs-6" />
                    </button>

                    {showResponsesModal && (
                      <div
                        className="custom-popover ak-text-sm project-preferences-popover"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="popover-content">
                          <div className="fw-bold mb-2 ak-text-md">
                            Customize Responses
                            <button
                              type="button"
                              className="project-info-button"
                              aria-label="Response option details"
                              title="Click for response option details"
                              onClick={() => setShowInfoTooltip((prev) => !prev)}
                            >
                              <i className="bi bi-info-circle" />
                            </button>
                          </div>
                          {showInfoTooltip && (
                            <div className="ak-text-xs mb-2 project-preferences-info">
                              Tone/Style changes how the answer sounds. Length
                              adjusts verbosity. AI Role sets the assistant
                              persona. Your Role gives context.
                            </div>
                          )}

                          <div className="settings-body">
                            {[
                              ["Tone / Style", "tone", TONE_MODE_OPTIONS],
                              ["Length of Responses", "length", LENGTH_OPTIONS],
                              ["AI Role", "aiRole", AI_ROLE_OPTIONS],
                              ["Your Role", "userRole", USER_ROLE_OPTIONS],
                            ].map(([label, key, options]) => (
                              <div className="settings-row" key={key}>
                                <span className="refresh-prompts ak-text-sm">
                                  {label}
                                </span>
                                <select
                                  value={localPrefs?.[key] || "default"}
                                  onChange={(e) =>
                                    handlePreferenceChange(key, e.target.value)
                                  }
                                >
                                  {options.map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}
                          </div>

                          <div className="d-flex gap-2 justify-content-center mt-2 align-items-center">
                            <button
                              type="button"
                              className="cancle-btn w-25 ak-text-sm py-1"
                              onClick={handleCancelPreferences}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="custom-btn w-25 ak-text-sm py-1"
                              onClick={handleApplyPreferences}
                            >
                              Apply
                            </button>
                            <button
                              type="button"
                              className="project-reset-button"
                              aria-label="Reset responses to default"
                              title="Reset to default"
                              onClick={() =>
                                setLocalPrefs(DEFAULT_AI_PREFERENCES || {})
                              }
                            >
                                <i className="bi bi-arrow-counterclockwise" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn p-0 chat-btn-message"
                    onClick={handleAttachClick}
                    title="Attach Files"
                  >
                    <i className="bi bi-paperclip fs-5" />
                  </button>
                  <button
                    type="button"
                    className="btn p-0 chat-btn-message"
                    onClick={toggleReadAloud}
                    title="Read Aloud"
                  >
                    <i
                      className={`bi ${
                        isSpeaking ? "bi-volume-mute" : "bi-volume-up"
                      } fs-5`}
                    />
                  </button>
                  <button
                    type="button"
                    className="btn p-0 chat-btn-message"
                    onClick={() => {
                      setMicOpen(true);
                      startRecording();
                    }}
                    title="Audio"
                  >
                    <i className="bi bi-mic fs-5" />
                  </button>

                  {micOpen && (
                    <div className="mic-overlay position-absolute start-50 translate-middle-x p-3">
                      <div className="mic-waveform d-flex gap-2 mb-2">
                        {[...Array(5)].map((_, index) => (
                          <span key={index} className="wave-dot" />
                        ))}
                      </div>
                      <div
                        className="live-preview mb-2 p-2 rounded"
                        style={{
                          background: "#f8f9fa",
                          height: "40px",
                          fontSize: "12px",
                          overflowY: "auto",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {isRecording
                          ? "Listening..."
                          : isProcessingSTT
                            ? "Processing..."
                            : interimTranscript}
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-sm d-flex align-items-center justify-content-center"
                          onClick={stopRecording}
                          disabled={isProcessingSTT}
                          title="Confirm"
                          type="button"
                          style={{
                            color: "var(--btn-color)",
                            backgroundColor: "var(--primary-color)",
                            minWidth: "36px",
                            opacity: isProcessingSTT ? 0.7 : 1,
                          }}
                        >
                          {isProcessingSTT ? (
                            <span
                              className="spinner-border spinner-border-sm text-light"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            <i className="bi bi-check-lg" />
                          )}
                        </button>
                        <button
                          className="btn btn-sm border"
                          onClick={cancelRecording}
                          disabled={isProcessingSTT}
                          title="Cancel"
                          type="button"
                          style={{
                            color: "var(--primary-color)",
                            backgroundColor: "var(--btn-color)",
                            opacity: isProcessingSTT ? 0.6 : 1,
                          }}
                        >
                          <i className="bi bi-x-lg" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="custom-send-btn m-0 p-0 d-flex align-items-center justify-content-center"
                  onClick={handleSendDraft}
                  disabled={
                    !draftMessage.trim() && messageAttachments.length === 0
                  }
                >
                  <img src={sendIcon} alt="Send" title="Send" />
                </button>
              </div>

              {/* <div className="project-chat-help">
                Messages and attachments stay inside this project.
              </div> */}

              <input
                ref={messageFileInputRef}
                type="file"
                className="d-none"
                multiple
                onChange={handleMessageFileChange}
              />
            </div>

            <div className="project-recents-section">
              <div className="project-section-title">
                <h2>Recents</h2>
              </div>

              {sessions.length === 0 ? (
                <div className="project-empty-recents">
                  <i className="bi bi-chat-square-text" />
                  <h3>No chats yet</h3>
                  <p>
                    Start a new conversation in this project to build history
                    here.
                  </p>
                </div>
              ) : (
                <div className="project-recents-list custom-ak-scollbar bg-danger ">
                  {sessions.map((session) => (
                    <div
                      key={session.session_id}
                      className="project-recent-row"
                    >
                      <button
                        type="button"
                        className="project-recent-open"
                        onClick={() => handleOpenChat(session.session_id)}
                      >
                        <span className="project-recent-icon">
                          <i className="bi bi-chat-left-text" />
                        </span>
                        <span className="project-recent-body">
                          <span className="project-recent-title">
                            {session.session_name?.trim() ||
                              getRecentChatLabel(session)}
                          </span>
                          <span className="project-recent-meta">
                            {/* <span>
                              {session.model_generic_name ||
                                session.model_name ||
                                "Project chat"}
                            </span> */}
                            <span>
                              {formatRelativeTime(session.session_start)}
                            </span>
                          </span>
                        </span>
                      </button>
                      <div className="project-recent-menu-wrapper">
                        <button
                          type="button"
                          className="project-recent-menu-button"
                          onClick={(e) =>
                            handleToggleRecentMenu(e, session.session_id)
                          }
                          aria-label="Chat actions"
                        >
                          <i className="bi bi-three-dots-vertical" />
                        </button>

                        {openRecentMenuId === session.session_id && (
                          <div className="project-recent-menu-popover">
                            <button
                              type="button"
                              onClick={(e) =>
                                handleOpenRenameSession(e, session)
                              }
                            >
                              <i className="bi bi-pencil me-2" />
                              Rename
                            </button>
                            <button
                              type="button"
                              onClick={(e) =>
                                handleOpenDeleteSession(e, session)
                              }
                              className="danger"
                            >
                              <i className="bi bi-trash me-2" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="project-context-panel">
            <div className="project-context-header">
              <div>
                <h2>Context</h2>
                <p>
                  {Math.round(contextUsagePercent)}% of Project capacity used
                </p>
              </div>

              <div className="project-context-actions">
                <button
                  type="button"
                  className="project-icon-button"
                  onClick={() => contextFileInputRef.current?.click()}
                  title="Add document"
                >
                  <i className="bi bi-plus-lg" />
                </button>
              </div>
            </div>

            <div className="project-context-usage">
              <div
                className="project-context-usage-fill"
                style={{ width: `${contextUsagePercent}%` }}
              />
            </div>

            <div className="project-context-summary">
              <span>{formatBytes(contextBytesUsed)} used</span>
              <span>{formatBytes(projectCapacityBytes)} total</span>
            </div>

            <div className="project-context-grid custom-ak-scollbar">
              {contextFiles.length === 0 ? (
                <div className="project-context-empty-state">
                  Add PDFs, documents, or other text to reference in this
                  project.
                </div>
              ) : (
                contextFiles.map((file) => {
                  const ext =
                    file.file_type || getFileExtLabel(file.original_filename);
                  const isActive =
                    (file.status === "uploading" ||
                      file.status === "processing") &&
                    file.progress !== 100;
                  const isFailed = file.status === "failed";
                  const isCancelled = file.status === "cancelled";
                  const isCompleted = file.status === "completed";

                  return (
                    <article
                      key={file.file_id}
                      className="project-context-card"
                    >
                      <div className="project-context-card-head">
                        <div>
                          <h3 title={file.original_filename}>
                            {file.original_filename}
                          </h3>
                          <p className="m-0" style={{ fontSize: "0.75rem" }}>
                            {isCompleted && (
                              <i className="bi bi-check-circle-fill text-success me-1" />
                            )}
                            {isFailed && (
                              <i className="bi bi-x-circle-fill text-danger me-1" />
                            )}
                            {isCancelled && (
                              <i className="bi bi-x-octagon-fill text-warning me-1" />
                            )}
                            {isActive && (
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                              />
                            )}
                            {file.status.charAt(0).toUpperCase() +
                              file.status.slice(1)}
                            {file.progress != null && isActive && (
                              <span className="ms-1">{file.progress}%</span>
                            )}
                          </p>
                        </div>
                        <span className="project-context-kind">
                          {file.original_filename
                            ?.split(".")
                            .pop()
                            ?.toUpperCase()}
                        </span>
                      </div>

                      {isActive && file.progress != null && (
                        <div
                          className="project-context-progress"
                          style={{
                            height: 3,
                            background: "var(--border-color, #e0e0e0)",
                            borderRadius: 2,
                            marginTop: 4,
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${file.progress}%`,
                              background: "var(--primary, #4f46e5)",
                              borderRadius: 2,
                              transition: "width 0.3s ease",
                            }}
                          />
                        </div>
                      )}

                      <div
                        className="project-context-card-foot"
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span>{formatBytes(file.file_size)}</span>
                        <div
                          className="d-flex gap-1"
                          style={{ fontSize: "0.75rem" }}
                        >
                          {isFailed && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              style={{ padding: "1px 6px", fontSize: "0.7rem" }}
                              title="Retry ingestion"
                              onClick={() => handleRetryContextFile(file)}
                            >
                              <i className="bi bi-arrow-clockwise" /> Retry
                            </button>
                          )}
                          {isActive && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              style={{ padding: "1px 6px", fontSize: "0.7rem" }}
                              title="Cancel ingestion"
                              onClick={() => handleCancelIngestion(file)}
                            >
                              <i className="bi bi-x-lg" /> Cancel
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            style={{ padding: "1px 6px", fontSize: "0.7rem" }}
                            title="Delete file"
                            onClick={() => handleDeleteContextFile(file)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                      </div>

                      {isFailed && file.failure_reason && (
                        <div
                          style={{
                            fontSize: "0.65rem",
                            color: "var(--danger, #dc3545)",
                            marginTop: 4,
                            wordBreak: "break-word",
                          }}
                        >
                          {file.failure_reason}
                        </div>
                      )}
                    </article>
                  );
                })
              )}
            </div>

            <input
              ref={contextFileInputRef}
              type="file"
              className="d-none"
              multiple
              onChange={handleContextFileChange}
            />
          </aside>
        </div>
      </div>

      {showSessionRenameModal && renameSessionTarget && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Rename Chat</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSessionRenameModal(false);
                    setRenameSessionTarget(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <label className="form-label">Chat name</label>
                <input
                  type="text"
                  className="form-control"
                  value={renameSessionValue}
                  onChange={(e) => setRenameSessionValue(e.target.value)}
                  placeholder="Enter a new chat name"
                />
              </div>
              <div className="modal-footer">
                <button
                  className="cancle-btn"
                  onClick={() => {
                    setShowSessionRenameModal(false);
                    setRenameSessionTarget(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="custom-btn delete-btn"
                  onClick={handleConfirmRenameSession}
                  disabled={savingSessionAction || !renameSessionValue.trim()}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSessionDeleteModal && deleteSessionTarget && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Chat</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSessionDeleteModal(false);
                    setDeleteSessionTarget(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <p className="m-0">
                  Delete this chat? This will remove the conversation from the
                  project recents list.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="cancle-btn"
                  onClick={() => {
                    setShowSessionDeleteModal(false);
                    setDeleteSessionTarget(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="custom-btn delete-btn"
                  onClick={handleConfirmDeleteSession}
                  disabled={savingSessionAction}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <CreateProjectModal project={project} onClose={handleEditClose} />
      )}

      {showDeleteModal && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Project</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowDeleteModal(false)}
                />
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{project.name}</strong>? Chats in this project will be
                  unlinked but not deleted.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="cancle-btn"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="custom-btn delete-btn"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectWorkspace;
